import type { CompanyAiPmContextItem } from '@/services/companyAiService';

export type CompanyAiFocusDomain = CompanyAiPmContextItem['domain'];
export type CompanyAiConversationTailItem = {
  role: string;
  messageText: string;
};

export type CompanyAiFollowUpSeed = {
  prompt: string;
  conversationTail: CompanyAiConversationTailItem[];
};

export type CompanyAiPeriodFilter = {
  domain: CompanyAiFocusDomain;
  label: string;
  startDate: string;
  endDate: string;
  dateBasis: string | null;
};

export type CompanyAiFocusTarget = {
  domain: CompanyAiFocusDomain;
  recordId: string | null;
  routePath: string;
  searchText: string;
  title: string;
  summary: string;
};

export type CompanyAiResolvedDateRange = {
  startDate?: string | null;
  endDate?: string | null;
  dateField?: string | null;
  sourcePhrase?: string | null;
};

export type CompanyAiNavigationState = {
  companyAiFocus?: CompanyAiFocusTarget;
  companyAiPeriodFilter?: CompanyAiPeriodFilter;
  companyAiFollowUp?: CompanyAiFollowUpSeed;
};

const DEFAULT_ROUTE_BY_DOMAIN: Record<CompanyAiFocusDomain, string> = {
  projects: '/projects',
  quotations: '/quotations',
  tasks: '/tasks',
  invoices: '/invoices',
  clients: '/clients',
  certificates: '/certificates',
  byda: '/ai',
};

function normalizeText(value: unknown) {
  return String(value || '').trim();
}

function normalizeRoutePath(routePath: string | null | undefined, domain: CompanyAiFocusDomain) {
  const normalized = normalizeText(routePath);
  if (normalized.startsWith('/')) {
    return normalized;
  }
  return DEFAULT_ROUTE_BY_DOMAIN[domain];
}

function normalizeRecordId(recordId: number | string | null | undefined) {
  const normalized = normalizeText(recordId);
  return normalized || null;
}

function getSummaryTitle(domain: CompanyAiFocusDomain) {
  switch (domain) {
    case 'projects':
      return 'Project summary';
    case 'quotations':
      return 'Quotation summary';
    case 'tasks':
      return 'Task summary';
    case 'invoices':
      return 'Invoice summary';
    case 'clients':
      return 'Client summary';
    case 'certificates':
      return 'Certificate summary';
    case 'byda':
    default:
      return 'BYDA summary';
  }
}

function normalizeDateBasisForDomain(domain: CompanyAiFocusDomain, dateField: string | null) {
  const normalized = normalizeText(dateField).toLowerCase();
  if (!normalized) {
    return null;
  }

  switch (domain) {
    case 'projects':
      return normalized === 'createdat' || normalized === 'createddate'
        ? 'created date'
        : 'updated date';
    case 'tasks':
      if (normalized === 'completeddate' || normalized === 'completedat') return 'completed date';
      if (normalized === 'startdate' || normalized === 'startedat') return 'start date';
      if (normalized === 'createdat' || normalized === 'createddate') return 'created date';
      if (normalized === 'updatedat' || normalized === 'updateddate' || normalized === 'modifieddate') return 'updated date';
      return 'due date';
    case 'invoices':
      if (normalized === 'paiddate') return 'paid date';
      if (normalized === 'duedate') return 'due date';
      return 'issue date';
    case 'quotations':
      return normalized === 'createddate' || normalized === 'createdat'
        ? 'created date'
        : 'issue date';
    case 'certificates':
      return 'issue date';
    default:
      return null;
  }
}

function extractPeriodFilter(item: CompanyAiPmContextItem): CompanyAiPeriodFilter | null {
  const title = normalizeText(item.title);
  const summary = normalizeText(item.summary);
  if (!summary || !/\bsummary\b/i.test(title)) {
    return null;
  }

  const periodMatch = summary.match(/\bperiod\s+(.+?)\s+\((\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})\)/i);
  if (!periodMatch) {
    return null;
  }

  const byMatch = summary.match(/\bby\s+([a-z ]+?date)\b/i);
  const latestMatch = summary.match(/\blatest\s+(updated|created|issued|due|paid)\b/i);
  const dateBasis = byMatch?.[1]?.trim().toLowerCase()
    || (latestMatch?.[1] ? `${latestMatch[1].toLowerCase()} date` : null);

  return {
    domain: item.domain,
    label: periodMatch[1].trim(),
    startDate: periodMatch[2],
    endDate: periodMatch[3],
    dateBasis,
  };
}

export function buildCompanyAiPeriodSummaryItem(
  domain: CompanyAiFocusDomain,
  dateRange: CompanyAiResolvedDateRange | null | undefined
): CompanyAiPmContextItem | null {
  const startDate = normalizeText(dateRange?.startDate);
  const endDate = normalizeText(dateRange?.endDate);
  if (!startDate || !endDate) {
    return null;
  }

  const label = normalizeText(dateRange?.sourcePhrase) || `${startDate} to ${endDate}`;
  const dateBasis = normalizeDateBasisForDomain(domain, normalizeText(dateRange?.dateField) || null);
  const summaryParts = [
    `period ${label} (${startDate} to ${endDate})`,
    dateBasis ? `by ${dateBasis}` : null,
  ].filter(Boolean);

  return {
    domain,
    recordId: null,
    title: getSummaryTitle(domain),
    summary: summaryParts.join('; '),
    routePath: DEFAULT_ROUTE_BY_DOMAIN[domain],
  };
}

function normalizeConversationTailItem(value: unknown): CompanyAiConversationTailItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const role = normalizeText((value as { role?: unknown }).role);
  const messageText = normalizeText((value as { messageText?: unknown }).messageText);
  if (!role || !messageText) {
    return null;
  }

  return {
    role,
    messageText,
  };
}

function extractSearchAnchor(item: CompanyAiPmContextItem) {
  if (!normalizeRecordId(item.recordId)) {
    return extractPeriodFilter(item) ? '' : normalizeText(item.summary);
  }

  const title = normalizeText(item.title);
  const headingMatch = title.match(/\b(?:Project|Quotation|Task|Invoice|Client|Certificate|BYDA)\s+([A-Z0-9-]+)/i);
  if (headingMatch?.[1]) {
    return headingMatch[1];
  }

  const titlePrefix = title.split(/\s+-\s+/, 2)[0]?.trim();
  if (titlePrefix) {
    return titlePrefix;
  }

  return normalizeText(item.summary);
}

export function buildCompanyAiFocusTarget(item: CompanyAiPmContextItem): CompanyAiFocusTarget {
  return {
    domain: item.domain,
    recordId: normalizeRecordId(item.recordId),
    routePath: normalizeRoutePath(item.routePath, item.domain),
    searchText: extractSearchAnchor(item),
    title: normalizeText(item.title),
    summary: normalizeText(item.summary),
  };
}

export function buildCompanyAiMatchNavigation(
  item: CompanyAiPmContextItem,
  options?: {
    prompt?: string | null;
    conversationTail?: Array<{
      role: string;
      messageText: string;
    }>;
  }
) {
  const focusTarget = buildCompanyAiFocusTarget(item);
  const prompt = normalizeText(options?.prompt);
  const companyAiFollowUp = prompt ? {
    prompt,
    conversationTail: Array.isArray(options?.conversationTail)
      ? options!.conversationTail.map((value) => normalizeConversationTailItem(value)).filter(Boolean) as CompanyAiConversationTailItem[]
      : [],
  } satisfies CompanyAiFollowUpSeed : undefined;

  return {
    pathname: focusTarget.routePath,
    state: {
      companyAiFocus: focusTarget,
      companyAiPeriodFilter: extractPeriodFilter(item) || undefined,
      companyAiFollowUp,
    } satisfies CompanyAiNavigationState,
  };
}

export function readCompanyAiFocusTarget(locationState: unknown, domain: CompanyAiFocusDomain) {
  if (!locationState || typeof locationState !== 'object') {
    return null;
  }

  const candidate = (locationState as CompanyAiNavigationState).companyAiFocus;
  if (!candidate || candidate.domain !== domain) {
    return null;
  }

  return {
    ...candidate,
    recordId: normalizeRecordId(candidate.recordId),
    routePath: normalizeRoutePath(candidate.routePath, candidate.domain),
    searchText: normalizeText(candidate.searchText),
    title: normalizeText(candidate.title),
    summary: normalizeText(candidate.summary),
  } satisfies CompanyAiFocusTarget;
}

export function readCompanyAiPeriodFilter(locationState: unknown, domain: CompanyAiFocusDomain) {
  if (!locationState || typeof locationState !== 'object') {
    return null;
  }

  const candidate = (locationState as CompanyAiNavigationState).companyAiPeriodFilter;
  if (!candidate || candidate.domain !== domain) {
    return null;
  }

  const startDate = normalizeText(candidate.startDate);
  const endDate = normalizeText(candidate.endDate);
  const label = normalizeText(candidate.label);
  if (!startDate || !endDate || !label) {
    return null;
  }

  return {
    domain: candidate.domain,
    label,
    startDate,
    endDate,
    dateBasis: normalizeText(candidate.dateBasis) || null,
  } satisfies CompanyAiPeriodFilter;
}

export function readCompanyAiFollowUpSeed(locationState: unknown) {
  if (!locationState || typeof locationState !== 'object') {
    return null;
  }

  const candidate = (locationState as CompanyAiNavigationState).companyAiFollowUp;
  const prompt = normalizeText(candidate?.prompt);
  if (!prompt) {
    return null;
  }

  return {
    prompt,
    conversationTail: Array.isArray(candidate?.conversationTail)
      ? candidate!.conversationTail.map((value) => normalizeConversationTailItem(value)).filter(Boolean) as CompanyAiConversationTailItem[]
      : [],
  } satisfies CompanyAiFollowUpSeed;
}

export function matchesCompanyAiFocusRecord(target: CompanyAiFocusTarget | null, recordId: unknown) {
  if (!target?.recordId) {
    return false;
  }
  return normalizeRecordId(recordId) === target.recordId;
}

export function getCompanyAiFocusDomId(domain: CompanyAiFocusDomain, recordId: unknown) {
  const normalizedRecordId = normalizeRecordId(recordId);
  if (!normalizedRecordId) {
    return null;
  }
  const safeToken = normalizedRecordId.replace(/[^a-z0-9_-]+/gi, '-').toLowerCase();
  return `company-ai-focus-${domain}-${safeToken}`;
}
